import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as HomeBuildingActions from './home-building.actions';
import { of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  selectProjects,
  selectProjectLots,
  selectProductList,
  selectCategoryList,
  selectHomeBuildingState,
  selectUofms,
  selectProjectLocations
} from './home-building.selectors';
import { DropdownData, LookupsService } from '@zayan-erp/shared';
import {
  GeographyService,
  MajorCodeService,
  OUnitCodeService,
  ProductCategoriesService,
  ProductListService,
  ProductSubcategoriesService
} from '@zayan-erp/home-building';
import { CityService } from 'libs/home-building/src/lib/services/city.service';

@Injectable()
export class HomeBuildingEffects {
  private actions$ = inject(Actions);
  private lookupsService = inject(LookupsService);
  private geographyService = inject(GeographyService);
  private productListService = inject(ProductListService);
  private productSubcategoriesService = inject(ProductSubcategoriesService);
  private productCategoriesService = inject(ProductCategoriesService);
  private uofmService = inject(MajorCodeService);
  private cityService = inject(CityService);

  store = inject(Store);
  projects$ = this.store.select(selectProjects);
  lots$ = this.store.select(selectProjectLots);
  productList$ = this.store.select(selectProductList);
  uofmList$ = this.store.select(selectUofms);
  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadProjects),
      withLatestFrom(this.projects$),
      switchMap(() =>
        this.lookupsService
          .readLookupProjects()
          .pipe(map(projects => HomeBuildingActions.loadProjectsSuccess({ projects })))
      )
    )
  );

  loadAddresses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadCities),
      switchMap(() =>
        this.geographyService.getAllCities().pipe(
          map(citiesList => {
            const cities = citiesList.map(
              city =>
                ({
                  id: city.id,
                  firstLabel: city.cityName,
                  secondLabel: city.cityCode
                }) as DropdownData
            );
            return HomeBuildingActions.loadCitiesSuccess({ cities });
          })
        )
      )
    )
  );

  loadLots$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadLots),
      withLatestFrom(this.lots$),
      switchMap(() =>
        this.lookupsService.readLookupLots().pipe(map(lots => HomeBuildingActions.loadLotsSuccess({ lots })))
      )
    )
  );

  selectedProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.selectedProject),
      // withLatestFrom(this.store.select(selectProjectLots)),
      withLatestFrom(this.store.select(selectProjectLots)),
      switchMap(([action, projectLots]) => {
        // Extract projectId from selectedProject object
        const projectId = action.projectId && action.projectId ? action.projectId : null;
        const force = action.force ?? false;
        const lots = projectLots;
        if (!force && projectId && lots && lots.length > 0) {
          // Already in state, just dispatch success
          return of(HomeBuildingActions.loadProjectLotsSuccess({ projectLots: { [projectId]: lots } }));
        } else if (projectId) {
          // Not in state, fetch from API
          return this.lookupsService.readLookupProjectLots(projectId ?? '').pipe(
            map(result => {
              const lots = result[projectId ?? ''] || [];
              return HomeBuildingActions.loadProjectLotsSuccess({ projectLots: { [projectId ?? '']: lots } });
            })
          );
        } else {
          // No projectId, return an empty observable (no action)
          return of();
        }
      })
    )
  );

  loadProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadProductList),
      withLatestFrom(this.productList$),
      switchMap(() =>
        this.productListService
          .readProductList()
          .pipe(
            map(productList =>
              productList.map(
                product =>
                  ({
                    id: product.id,
                    firstLabel: product.name,
                    secondLabel: product.code
                  }) as DropdownData
              )
            )
          )
          .pipe(map(productList => HomeBuildingActions.loadProductListSuccess({ productList: productList })))
      )
    )
  );

  loadUofms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadUofms),
      withLatestFrom(this.uofmList$),
      switchMap(() =>
        this.uofmService.readUnitOfMeasures().pipe(
          map(uofms => {
            return HomeBuildingActions.loadUofmsSuccess({ uofms: uofms });
          })
        )
      )
    )
  );

  loadPrjectLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadProjectLocations),
      withLatestFrom(this.store.select(selectProjectLocations)),
      switchMap(() => {
        return this.lookupsService.readLocations().pipe(
          map(locations => {
            return HomeBuildingActions.loadProjectLocationsSuccess({ locations: locations });
          })
        );
      })
    )
  );

  loadCategoryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadCategoryList),
      withLatestFrom(this.store.select(selectCategoryList)),
      switchMap(([action, cachedCategoryList]) => {
        return of(HomeBuildingActions.loadCategoryListSuccess({ categoryList: cachedCategoryList }));
      })
    )
  );

  loadCategoryListByProductListId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadCategoryListByProductListId),
      withLatestFrom(this.store.select(selectHomeBuildingState)),
      switchMap(([action, state]) => {
        const cachedCategories = state.categoryListByProductListId[action.productListId];
        if (!action.force && cachedCategories && cachedCategories.length > 0) {
          return of(
            HomeBuildingActions.loadCategoryListByProductListIdSuccess({
              categoryListByProductListId: { [action.productListId]: cachedCategories }
            })
          );
        } else {
          return this.productCategoriesService.readProductCategoriesByProductListId(action.productListId).pipe(
            map(categoryListByProductListId => {
              const mappedCategories = categoryListByProductListId.map(category => ({
                id: category.id || '',
                firstLabel: category.code,
                secondLabel: category.description
              }));
              return HomeBuildingActions.loadCategoryListByProductListIdSuccess({
                categoryListByProductListId: { [action.productListId]: mappedCategories }
              });
            })
          );
        }
      })
    )
  );

  loadSubcategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadSubcategories),
      withLatestFrom(this.store.select(selectHomeBuildingState)),
      switchMap(([action, state]) => {
        const cachedSubcategories = state.subcategories[action.categoryListId];
        if (!action.force && cachedSubcategories && cachedSubcategories.length > 0) {
          return of(
            HomeBuildingActions.loadSubcategoriesSuccess({
              subcategories: { [action.categoryListId]: cachedSubcategories }
            })
          );
        } else {
          return this.productSubcategoriesService.readSubcategories(action.productListId, action.categoryListId).pipe(
            map((subcategories: any[]) => {
              const mappedSubcategories = subcategories.map((subcategory: any) => ({
                id: subcategory.id || '',
                firstLabel: subcategory.code,
                secondLabel: subcategory.description
              }));
              return HomeBuildingActions.loadSubcategoriesSuccess({
                subcategories: { [action.categoryListId]: mappedSubcategories }
              });
            })
          );
        }
      })
    )
  );

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadCountries),
      switchMap(() =>
        this.lookupsService
          .readCountries()
          .pipe(map(countries => HomeBuildingActions.loadCountriesSuccess({ countries })))
      )
    )
  );

  loadCitiesByCountryId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeBuildingActions.loadCitiesByCountryId),
      switchMap(action => {
        if (action.countryId) {
          return this.cityService
            .readCitiesByCountryId(action.countryId)
            .pipe(map(cities => HomeBuildingActions.loadCitiesByCountryIdSuccess({ cities })));
        } else {
          return of(HomeBuildingActions.loadCitiesByCountryIdSuccess({ cities: [] }));
        }
      })
    )
  );
}
